import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_URL_TOKEN, TenantDeviceAllowlist } from '@raptor-fe/core';
import { Observable } from 'rxjs';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';

/**
 * Client for the tenant-admin-scoped device allowlist endpoints. The caller's tenant
 * is resolved from their authentication claim server-side; no tenantId is sent.
 *
 * Backed by TenantAdminDeviceAllowlistController on /api/v1/tenantadmin/tenantdeviceallowlist.
 */
@Injectable({
    providedIn: 'root',
})
export class TenantAdminDeviceAllowlistService {
    private readonly http = inject(HttpClient);
    private readonly base_url = `${inject(BASE_URL_TOKEN)}v1/tenantadmin/tenantdeviceallowlist`;
    private httpHeadersWithNoLoading = LoadingHeaders.httpHeadersWithNoLoading();

    /**
     * Lists all approved devices for the caller's tenant.
     */
    getAll(): Observable<TenantDeviceAllowlist[]> {
        return this.http.get<TenantDeviceAllowlist[]>(this.base_url, {
            headers: this.httpHeadersWithNoLoading,
        });
    }

    /**
     * Adds a device to the caller-tenant's allowlist. Idempotent on deviceId;
     * re-adding preserves the original name.
     */
    add(deviceId: string, name: string): Observable<TenantDeviceAllowlist> {
        return this.http.post<TenantDeviceAllowlist>(this.base_url, { deviceId, name }, { headers: this.httpHeadersWithNoLoading });
    }

    /**
     * Removes a device from the caller-tenant's allowlist via soft-delete.
     */
    remove(deviceId: string): Observable<void> {
        return this.http.delete<void>(`${this.base_url}/${encodeURIComponent(deviceId)}`, {
            headers: this.httpHeadersWithNoLoading,
        });
    }
}
